import { useCallback, useEffect, useRef, useState } from "react";

import { streamTranscription, EVENT } from "@/api/stream";
import type { ConversationAttachment, VoiceNoteAccepted } from "@/api/types";
import { apiFetch } from "@/api/client";
import {
  MAX_RECORDING_MS,
  extensionFor,
  formatDuration,
  preferredMimeType,
  recorderErrorMessage,
  recordingSupported,
} from "./recorder";

/**
 * Enregistrer, envoyer, suivre la transcription (TICKET V1.5-10 §14, §24).
 *
 * Le mode retenu est le **B** du ticket : la transcription s'affiche et se
 * corrige avant l'envoi. C'est ce qui rend la voix utilisable sur un chantier —
 * un numéro de pièce mal entendu se répare en deux caractères, alors qu'une
 * question envoyée sur un mauvais MPN part chercher le mauvais produit et
 * revient avec une réponse juste sur la mauvaise chose.
 *
 * Rien ici ne comprend la question. Le transcript devient le texte du champ de
 * saisie, et l'envoi passe par le même chemin qu'une question tapée.
 */

export type VoiceState =
  | "idle"
  | "unsupported"
  | "recording"
  | "uploading"
  | "transcribing"
  | "ready"
  | "failed";

export interface VoiceNote {
  state: VoiceState;
  /** La durée d'enregistrement en cours, déjà mise en forme. */
  elapsed: string;
  error: string | null;
  /** La pièce jointe acceptée, une fois montée. */
  attachment: ConversationAttachment | null;
  /** Ce que le moteur a entendu. À corriger avant d'envoyer. */
  transcript: string;
  start: () => Promise<void>;
  stop: () => void;
  cancel: () => void;
  retry: () => Promise<void>;
  reset: () => void;
}

export function useVoiceNote(
  conversationId: string,
  onTranscript: (text: string) => void,
): VoiceNote {
  const [state, setState] = useState<VoiceState>(() =>
    recordingSupported() ? "idle" : "unsupported",
  );
  const [error, setError] = useState<string | null>(null);
  const [attachment, setAttachment] = useState<ConversationAttachment | null>(null);
  const [transcript, setTranscript] = useState("");
  const [elapsedMs, setElapsedMs] = useState(0);

  const recorder = useRef<MediaRecorder | null>(null);
  const chunks = useRef<Blob[]>([]);
  const startedAt = useRef(0);
  const ticker = useRef<number | null>(null);
  const abort = useRef<AbortController | null>(null);
  // Annulé : on jette ce qui a été capté. Sans ce drapeau, `onstop` monterait
  // l'enregistrement que quelqu'un vient justement de refuser.
  const cancelled = useRef(false);

  const stopTicker = useCallback(() => {
    if (ticker.current !== null) {
      window.clearInterval(ticker.current);
      ticker.current = null;
    }
  }, []);

  const releaseStream = useCallback(() => {
    recorder.current?.stream.getTracks().forEach((track) => track.stop());
    recorder.current = null;
  }, []);

  useEffect(
    () => () => {
      stopTicker();
      abort.current?.abort();
      releaseStream();
    },
    [releaseStream, stopTicker],
  );

  const follow = useCallback(
    async (accepted: VoiceNoteAccepted) => {
      abort.current?.abort();
      const controller = new AbortController();
      abort.current = controller;
      try {
        for await (const event of streamTranscription(
          conversationId,
          accepted.id,
          controller.signal,
        )) {
          if (event.type === EVENT.transcriptionCompleted) {
            const text = String(event.payload.transcript ?? "");
            // Tronqué : le flux est plafonné à 8 Ko, et un transcript long y
            // arrive coupé. On relit alors la pièce jointe, qui fait foi.
            if (event.payload.transcript_truncated) {
              const full = await apiFetch<ConversationAttachment>(
                `/assistant/conversations/${conversationId}/audio/${accepted.id}`,
              );
              setTranscript(full.transcript ?? text);
              setAttachment(full);
              onTranscript(full.transcript ?? text);
            } else {
              setTranscript(text);
              onTranscript(text);
            }
            setState("ready");
            return;
          }
          if (event.type === EVENT.transcriptionFailed) {
            setError(String(event.payload.message ?? "La transcription n'a pas abouti."));
            setState("failed");
            return;
          }
        }
        // Le flux s'est fermé sans verdict : on relit l'état, qui fait foi.
        const current = await apiFetch<ConversationAttachment>(
          `/assistant/conversations/${conversationId}/audio/${accepted.id}`,
        );
        setAttachment(current);
        if (current.status === "ANALYSED") {
          setTranscript(current.transcript ?? "");
          onTranscript(current.transcript ?? "");
          setState("ready");
        } else {
          setError(current.error ?? "La transcription n'a pas abouti.");
          setState("failed");
        }
      } catch (caught) {
        if (controller.signal.aborted) return;
        setError((caught as { message?: string })?.message ?? "La transcription a été interrompue.");
        setState("failed");
      }
    },
    [conversationId, onTranscript],
  );

  const upload = useCallback(
    async (blob: Blob, mimeType: string) => {
      setState("uploading");
      const form = new FormData();
      form.append("file", blob, `message.${extensionFor(mimeType)}`);
      try {
        const accepted = await apiFetch<VoiceNoteAccepted>(
          `/assistant/conversations/${conversationId}/audio`,
          { method: "POST", formData: form },
        );
        setAttachment(accepted);
        setState("transcribing");
        await follow(accepted);
      } catch (caught) {
        setError(
          (caught as { message?: string })?.message ??
            "L'enregistrement n'a pas pu être envoyé.",
        );
        setState("failed");
      }
    },
    [conversationId, follow],
  );

  const start = useCallback(async () => {
    if (!recordingSupported()) {
      setState("unsupported");
      return;
    }
    if (!navigator.onLine) {
      // Hors ligne (§23) : pas de file d'attente pour un enregistrement.
      // Afficher « envoyé » pour un fichier qui n'est jamais parti serait un
      // mensonge, et sur un chantier c'est celui qui coûte un aller-retour.
      setError("Hors ligne : le message vocal ne peut pas être envoyé. Le texte, oui.");
      setState("failed");
      return;
    }
    setError(null);
    setTranscript("");
    setAttachment(null);
    cancelled.current = false;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = preferredMimeType();
      const media = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      chunks.current = [];
      media.ondataavailable = (event) => {
        if (event.data.size > 0) chunks.current.push(event.data);
      };
      media.onstop = () => {
        stopTicker();
        releaseStream();
        const captured = chunks.current;
        chunks.current = [];
        if (cancelled.current || captured.length === 0) {
          setState("idle");
          setElapsedMs(0);
          return;
        }
        void upload(new Blob(captured, { type: media.mimeType || mimeType }), media.mimeType);
      };
      recorder.current = media;
      startedAt.current = Date.now();
      setElapsedMs(0);
      setState("recording");
      media.start();
      ticker.current = window.setInterval(() => {
        const elapsed = Date.now() - startedAt.current;
        setElapsedMs(elapsed);
        // La même borne que le serveur : arrêter ici évite de faire monter
        // trois minutes d'audio pour se les faire refuser.
        if (elapsed >= MAX_RECORDING_MS && media.state === "recording") media.stop();
      }, 200);
    } catch (caught) {
      setError(recorderErrorMessage(caught));
      setState("failed");
    }
  }, [releaseStream, stopTicker, upload]);

  const stop = useCallback(() => {
    if (recorder.current?.state === "recording") recorder.current.stop();
  }, []);

  const cancel = useCallback(() => {
    cancelled.current = true;
    abort.current?.abort();
    if (recorder.current?.state === "recording") {
      recorder.current.stop();
    } else {
      stopTicker();
      releaseStream();
      setState("idle");
      setElapsedMs(0);
    }
    setTranscript("");
    setAttachment(null);
    setError(null);
  }, [releaseStream, stopTicker]);

  const retry = useCallback(async () => {
    if (!attachment) return;
    setError(null);
    setState("transcribing");
    try {
      const accepted = await apiFetch<VoiceNoteAccepted>(
        `/assistant/conversations/${conversationId}/audio/${attachment.id}/retranscribe`,
        { method: "POST" },
      );
      await follow(accepted);
    } catch (caught) {
      setError((caught as { message?: string })?.message ?? "La reprise n'a pas abouti.");
      setState("failed");
    }
  }, [attachment, conversationId, follow]);

  const reset = useCallback(() => {
    abort.current?.abort();
    setState(recordingSupported() ? "idle" : "unsupported");
    setError(null);
    setTranscript("");
    setAttachment(null);
    setElapsedMs(0);
  }, []);

  return {
    state,
    elapsed: formatDuration(elapsedMs),
    error,
    attachment,
    transcript,
    start,
    stop,
    cancel,
    retry,
    reset,
  };
}
